import { useState } from "react";
import { Link } from "react-router";
import { useTranslation } from "react-i18next";
import { useCenter, useCenterCourses, formatAcademicYear, useSessionPreferences } from "../../../../center";
import { PatientOwnershipBadge } from "../PatientOwnership";
import { User, Trash2, Phone } from "lucide-react";
import Button from "../../../../../shared/ui/button/Button";
import { Surface } from "../../../../../shared/ui/primitives";
import { EnvelopeIcon } from "../../../../../icons";
import EditPatientModal from "./EditPatientModal";
import { useOtherCourses } from "../../../application/useOtherCourses";
import { usePatientGroups } from "../../../application/useGroupPatients";
import { groupLabel } from "../../../domain/GroupPatient";
import { countryName } from "../../../../../shared/domain/countries";

import { refusesWhatsApp, type Patient } from "../../../domain/Patient";
import WhatsAppIcon from "../../../../../shared/ui/icons/WhatsAppIcon";

interface PatientDetailsProps {
  therapistID: string;
  patient?: Patient;
  onSuccess?: () => void;
  onDelete?: () => void;
  deleting?: boolean;
}

export default function PatientDetails({
  therapistID,
  patient,
  onSuccess,
  onDelete,
  deleting = false
}: PatientDetailsProps) {
  const { t, i18n } = useTranslation();
  // Whose patient this is — only meaningful inside a centre.
  const { center } = useCenter();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  // A training centre's student carries a course; its name is the centre's.
  const isTraining = center?.type === "training";
  const { courses } = useCenterCourses(center?.id, isTraining && Boolean(patient?.course_id));
  const otherCourses = useOtherCourses(patient?.id, isTraining && Boolean(patient?.course_id));
  // The groups they are in, where the centre runs groups at all.
  const { modes } = useSessionPreferences();
  const { groups } = usePatientGroups(patient?.id, modes.group);

  if (!patient) return null;
  const course = courses.find((c) => c.id === patient.course_id);

  const getPronounsLabel = (pronouns?: string) => {
    if (!pronouns) return "—";
    switch (pronouns.toLowerCase()) {
      case "he/him": return t("patient.pronounsHeHim");
      case "she/her": return t("patient.pronounsSheHer");
      case "they/them": return t("patient.pronounsTheyThem");
      default: return pronouns;
    }
  };

  return (
    <>
      <Surface>
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-lg font-semibold text-content-primary flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-accent-tint text-accent-text flex items-center justify-center">
              <User aria-hidden="true" className="w-4 h-4" />
            </div>
            {t("patient.details")}
          </h3>
          <Button size="sm" variant="outline" className="text-content-tertiary hover:text-brand-500 hover:bg-brand-50" onClick={() => setIsEditModalOpen(true)}>
            {t("common.edit")}
          </Button>
        </div>

        <div className="space-y-6">
          <div className="flex flex-col gap-1">
            <span className="text-xs font-bold text-content-tertiary uppercase tracking-widest">{t("patient.name")}</span>
            <h2 className="text-xl font-bold text-content-primary leading-tight">
              {patient.first_name} {patient.last_name}
            </h2>
            {center && (
              <PatientOwnershipBadge
                heldByCenter={Boolean(patient.center_id)}
                className="mt-1 self-start"
              />
            )}
          </div>

          <div className="space-y-4 pt-4 border-t border-border-subtle">
            <div className="flex flex-col gap-1">
              <span className="text-xs font-bold text-content-tertiary uppercase tracking-widest">{t("patient.email")}</span>
              <div className="flex items-center gap-2 text-content-secondary">
                <EnvelopeIcon className="w-4 h-4 text-content-tertiary" />
                <span className="text-sm font-medium">{patient.contact_email || "—"}</span>
              </div>
            </div>

            <div className="flex flex-col gap-1">
              <span className="text-xs font-bold text-content-tertiary uppercase tracking-widest">{t("patient.phone")}</span>
              <div className="flex items-center gap-2 text-content-secondary">
                <Phone aria-hidden="true" className="w-4 h-4 text-content-tertiary" />
                <span className="text-sm font-medium">{patient.contact_phone || "—"}</span>
              </div>
            </div>

            <div className="flex flex-col gap-1">
              <span className="text-xs font-bold text-content-tertiary uppercase tracking-widest">{t("patient.whatsapp")}</span>
              <div className="flex items-center gap-2 text-content-secondary">
                <WhatsAppIcon className="w-4 h-4 text-green-600 dark:text-green-400" />
                <span className="text-sm font-medium">{patient.whatsapp_phone || "—"}</span>
              </div>
              {refusesWhatsApp(patient) && (
                <span
                  className="self-start rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700 dark:bg-amber-500/10 dark:text-amber-400"
                  title={t("patient.whatsappOffHint")}
                >
                  {t("patient.whatsappOff")}
                </span>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="flex flex-col gap-1">
                <span className="text-xs font-bold text-content-tertiary uppercase tracking-widest">{t("patient.dateOfBirth")}</span>
                <span className="text-sm font-medium text-content-secondary">
                  {patient.date_of_birth ? new Date(patient.date_of_birth).toLocaleDateString() : "—"}
                </span>
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-xs font-bold text-content-tertiary uppercase tracking-widest">{t("patient.sex")}</span>
                <span className="text-sm font-medium text-content-secondary">
                  {patient.sex ? t(`patient.sex${patient.sex.charAt(0).toUpperCase() + patient.sex.slice(1)}`) : "—"}
                </span>
              </div>
            </div>

            <div className="flex flex-col gap-1">
              <span className="text-xs font-bold text-content-tertiary uppercase tracking-widest">{t("patient.pronouns")}</span>
              <span className="text-sm font-medium text-content-secondary">{getPronounsLabel(patient.pronouns)}</span>
            </div>

            {isTraining && (
              <div className="flex flex-col gap-1">
                <span className="text-xs font-bold text-content-tertiary uppercase tracking-widest">{t("patient.course.title")}</span>
                <span className="text-sm font-medium text-content-secondary">
                  {patient.course_id
                    ? [
                        course?.internal_name ?? "…",
                        patient.academic_year
                          ? formatAcademicYear(patient.academic_year)
                          : null,
                      ]
                        .filter(Boolean)
                        .join(" · ")
                    : t("patient.course.none")}
                </span>
                {otherCourses.length > 0 && (
                  <div className="mt-2 flex flex-col gap-1">
                    <span className="text-xs text-content-tertiary">{t("patient.course.alsoMentoredIn")}</span>
                    <ul className="flex flex-col gap-1">
                      {otherCourses.map((other) => (
                        <li key={other.patient_id}>
                          <Link
                            to={`/patient/${other.patient_id}`}
                            className="text-sm font-medium text-brand-600 underline-offset-2 hover:underline dark:text-brand-300"
                          >
                            {[other.internal_name, other.academic_year ? formatAcademicYear(other.academic_year) : null]
                              .filter(Boolean)
                              .join(" · ")}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {groups.length > 0 && (
              <div className="flex flex-col gap-1">
                <span className="text-xs font-bold text-content-tertiary uppercase tracking-widest">{t("patient.groups.title")}</span>
                <ul className="flex flex-wrap gap-2">
                  {groups.map((group) => (
                    <li key={group.id}>
                      <Link
                        to={`/group-patient/${group.id}`}
                        className="inline-flex rounded-full border border-brand-100 bg-brand-50 px-2.5 py-0.5 text-sm font-medium text-brand-600 hover:underline dark:border-brand-500/20 dark:bg-brand-500/10 dark:text-brand-300"
                      >
                        {groupLabel(group)}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="flex flex-col gap-1 pt-4 border-t border-border-subtle">
              <span className="text-xs font-bold text-content-tertiary uppercase tracking-widest">{t("patient.address")}</span>
              <div className="text-sm font-medium text-content-secondary space-y-0.5">
                {patient.street && <p>{patient.street}</p>}
                {(patient.city || patient.state || patient.postal_code) && (
                  <p>
                    {patient.postal_code && `${patient.postal_code} `}
                    {patient.city}{patient.city && patient.state ? ', ' : ''}{patient.state}
                  </p>
                )}
                <p>{countryName(patient.country || "ES", i18n.language)}</p>
                {patient.timezone && (
                  <p className="text-content-tertiary">{patient.timezone}</p>
                )}
                {!patient.street && !patient.city && !patient.state && !patient.postal_code && <p>—</p>}
              </div>
            </div>
          </div>
        </div>

        {/* Delete Patient Section */}
        {onDelete && (
          <div className="mt-8 pt-6 border-t border-red-100 dark:border-red-900/20">
            <Button
              variant="outline"
              className="w-full text-red-500 hover:text-red-600 hover:bg-red-50 border-red-200 hover:border-red-300 dark:border-red-900/40 dark:hover:bg-red-950/30 transition-all"
              onClick={onDelete}
              loading={deleting}
              startIcon={<Trash2 className="w-3.5 h-3.5" />}
              size="sm"
            >
              {deleting
                ? t("common.deleting")
                : t("patient.deletePatient")}
            </Button>
          </div>
        )}
      </Surface>

      {/* Edit Modal */}
      {isEditModalOpen && (
        <EditPatientModal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          patient={patient}
          therapistID={therapistID}
          onSuccess={() => {
            if (onSuccess) onSuccess();
          }}
        />
      )}
    </>
  );
}
